import { isEnumValue } from "@redotech/util/enum";
import { GofoServices } from "./carrier-service-catalog";

/**
 * GOFO/CIRRO E-Commerce injection hubs accepted by EasyPost's `ParcllAccount`
 * `origin_hub` credential. EasyPost does not validate the value server-side;
 * this list mirrors the select in the EasyPost dashboard.
 */
export const GOFO_ORIGIN_HUBS = [
  { code: "EA", label: "East (Atlanta)" },
  { code: "WE", label: "West (Los Angeles)" },
  { code: "CE", label: "Central (Chicago)" },
  { code: "NE", label: "Northeast (New York)" },
  { code: "SE", label: "Southeast (Miami)" },
  { code: "SO", label: "South (Dallas)" },
  { code: "NJ", label: "New Jersey" },
  { code: "SL", label: "Salt Lake City" },
  { code: "CA", label: "Canada" },
] as const;

export type GofoOriginHubCode = (typeof GOFO_ORIGIN_HUBS)[number]["code"];

export enum GofoByocaOriginHub {
  ATL = "ATL",
  CLT = "CLT",
  DEN = "DEN",
  DFW = "DFW",
  EWR = "EWR",
  IAH = "IAH",
  IND = "IND",
  JFK = "JFK",
  LAX = "LAX",
  MIA = "MIA",
  ORD = "ORD",
  PHX = "PHX",
  SEA = "SEA",
  SFO = "SFO",
  SLC = "SLC",
}

const GOFO_BYOCA_ORIGIN_HUB_LABELS: Record<GofoByocaOriginHub, string> = {
  [GofoByocaOriginHub.ATL]: "Atlanta",
  [GofoByocaOriginHub.CLT]: "Charlotte",
  [GofoByocaOriginHub.DEN]: "Denver",
  [GofoByocaOriginHub.DFW]: "Dallas / Fort Worth",
  [GofoByocaOriginHub.EWR]: "Newark",
  [GofoByocaOriginHub.IAH]: "Houston",
  [GofoByocaOriginHub.IND]: "Indianapolis",
  [GofoByocaOriginHub.JFK]: "New York City",
  [GofoByocaOriginHub.LAX]: "Los Angeles",
  [GofoByocaOriginHub.MIA]: "Miami",
  [GofoByocaOriginHub.ORD]: "Chicago",
  [GofoByocaOriginHub.PHX]: "Phoenix",
  [GofoByocaOriginHub.SEA]: "Seattle",
  [GofoByocaOriginHub.SFO]: "San Francisco",
  [GofoByocaOriginHub.SLC]: "Salt Lake City",
};

export const GOFO_BYOCA_ORIGIN_HUBS: readonly {
  code: GofoByocaOriginHub;
  label: string;
}[] = Object.values(GofoByocaOriginHub).map((code) => ({
  code,
  label: GOFO_BYOCA_ORIGIN_HUB_LABELS[code],
}));

/** The Regional code an EasyPost GOFO account emits at each hub, which its direct replacement keeps. */
const GOFO_REGIONAL_SERVICE_BY_HUB: Record<
  GofoByocaOriginHub,
  GofoServices | null
> = {
  [GofoByocaOriginHub.ATL]: GofoServices.RegionalEast,
  [GofoByocaOriginHub.CLT]: GofoServices.RegionalCharlotte,
  [GofoByocaOriginHub.DEN]: GofoServices.RegionalDenver,
  [GofoByocaOriginHub.DFW]: GofoServices.RegionalSouth,
  [GofoByocaOriginHub.EWR]: GofoServices.RegionalNewJersey,
  [GofoByocaOriginHub.IAH]: null,
  [GofoByocaOriginHub.IND]: null,
  [GofoByocaOriginHub.JFK]: GofoServices.RegionalNortheast,
  [GofoByocaOriginHub.LAX]: GofoServices.RegionalWest,
  [GofoByocaOriginHub.MIA]: GofoServices.RegionalSoutheast,
  [GofoByocaOriginHub.ORD]: GofoServices.RegionalCentral,
  [GofoByocaOriginHub.PHX]: null,
  [GofoByocaOriginHub.SEA]: null,
  [GofoByocaOriginHub.SFO]: GofoServices.RegionalSanFrancisco,
  [GofoByocaOriginHub.SLC]: null,
};

const GOFO_ENTRY_PORT_BY_REGIONAL_SERVICE = new Map<
  GofoServices,
  GofoByocaOriginHub
>();
for (const hub of Object.values(GofoByocaOriginHub)) {
  const service = GOFO_REGIONAL_SERVICE_BY_HUB[hub];
  if (service !== null) {
    GOFO_ENTRY_PORT_BY_REGIONAL_SERVICE.set(service, hub);
  }
}

export function isGofoByocaOriginHub(
  value: unknown,
): value is GofoByocaOriginHub {
  return isEnumValue(value, GofoByocaOriginHub);
}

export function gofoRegionalService(
  entryPort: GofoByocaOriginHub,
): GofoServices | null {
  return GOFO_REGIONAL_SERVICE_BY_HUB[entryPort];
}

export function gofoEntryPortForRegionalService(
  service: GofoServices,
): GofoByocaOriginHub | null {
  return GOFO_ENTRY_PORT_BY_REGIONAL_SERVICE.get(service) ?? null;
}

/** True only for the Regional codes, which price by USPS zone like their EasyPost twin. */
export function isGofoRegionalService(service: GofoServices): boolean {
  return GOFO_ENTRY_PORT_BY_REGIONAL_SERVICE.has(service);
}
